import { DataBlockTypeDto, SexType } from '@lmt/shared-types';
import { MeasuredValue } from '@lmt/unicorn-shared/app/features/measurements/types';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { checkMiniScreenPdfFile } from './file-operations';
import { parseDate } from './parsing';

const miniscreenFields: Record<
  string,
  {
    code: string;
    name: string;
  }
> = {
  patientNumber: {
    code: '0.FG.0.3',
    name: 'patient number',
  },
  template: {
    code: '0.OR.0.1',
    name: 'template name',
  },
  pdfFile: {
    code: '0.OR.0.0',
    name: 'PDF file',
  },
  firstName: {
    code: '0.FG.0.1',
    name: 'patient first name',
  },
  lastName: {
    code: '0.FG.0.0',
    name: 'patient last name',
  },
  startDate: {
    code: '0.FG.1.0',
    name: 'measurement start time',
  },
  endDate: {
    code: '0.FG.1.1',
    name: 'measurement end time',
  },
  dateOfBirth: {
    code: '0.FG.0.2',
    name: 'patient date of birth',
  },
  sex: {
    code: '0.FG.0.6',
    name: 'patient sex',
  },
};

export interface PatientInfo {
  number: string;
  firstName: string;
  lastName: string;
  startDate: Date;
  endDate: Date;
  dateOfBirth: Date | null;
  sex: SexType | null;
}

export async function getPatientInfoFromMeasuredValues(
  measuredValues: MeasuredValue[],
): Promise<PatientInfo> {
  return {
    number: tryGetValue(
      measuredValues,
      miniscreenFields.patientNumber,
    ) as string,
    firstName: tryGetValue(
      measuredValues,
      miniscreenFields.firstName,
    ) as string,
    lastName: tryGetValue(measuredValues, miniscreenFields.lastName) as string,
    startDate: tryGetValue(measuredValues, miniscreenFields.startDate) as Date,
    endDate: tryGetValue(measuredValues, miniscreenFields.endDate) as Date,
    dateOfBirth: tryGetValue(
      measuredValues,
      miniscreenFields.dateOfBirth,
    ) as Date | null,
    sex: tryGetValue(measuredValues, miniscreenFields.sex) as SexType,
  };
}

function tryGetValue(
  measuredValues: MeasuredValue[],
  field: { code: string; name: string },
) {
  try {
    const value = getMeasuredValue(measuredValues, field.code, field.name);
    switch (field.code) {
      case miniscreenFields.startDate.code:
      case miniscreenFields.endDate.code:
        return parseDate(value);
      case miniscreenFields.sex.code:
        return getSexValue(value);
      case miniscreenFields.dateOfBirth.code:
        return getNullableDateValue(
          measuredValues,
          miniscreenFields.dateOfBirth.code,
          'patient date of birth',
        );
      default:
        return value;
    }
  } catch (error) {
    throw `Unable to parse ${field.name} from mapping field ${field.code}. ${error}`;
  }
}

export function getTemplateName(measuredValues: MeasuredValue[]) {
  return getMeasuredValue(
    measuredValues,
    miniscreenFields.template.code,
    miniscreenFields.template.name,
  );
}

export function getMeasuredValue(
  measuredValues: MeasuredValue[],
  code: string,
  errorTextKey: string,
) {
  const measuredValue = measuredValues.find(
    (measuredValue) => measuredValue.key === code,
  );
  if (measuredValue) {
    return measuredValue.value;
  }

  throw Error(`Can't find ${errorTextKey} in measurement file.`);
}

function getNullableDateValue(
  measuredValues: MeasuredValue[],
  code: string,
  errorTextKey: string,
) {
  const value = getMeasuredValue(measuredValues, code, errorTextKey);
  if (value) {
    return parseDate(value);
  }
  return null;
}

export function getTemplateMeasurementType(
  measuredValues: MeasuredValue[],
  types: DataBlockTypeDto[],
): DataBlockTypeDto | undefined {
  const templateMeasuredValue = measuredValues.find(
    (measuredValue) => measuredValue.key === miniscreenFields.template.code,
  );
  if (templateMeasuredValue && templateMeasuredValue.value) {
    const template = templateMeasuredValue.value.toLowerCase();
    const measurementType = types.find((type) =>
      template.includes(type.value.toLowerCase()),
    );
    return measurementType;
  }
  return undefined;
}

export async function getPdfFilePath(
  measuredValues: MeasuredValue[],
): Promise<string | undefined> {
  if (isElectron()) {
    const templateMeasuredValue = measuredValues.find(
      (measuredValue) => measuredValue.key === miniscreenFields.pdfFile.code,
    );
    if (templateMeasuredValue && templateMeasuredValue.value) {
      const result = await checkMiniScreenPdfFile(templateMeasuredValue.value);
      return result.type === 'success'
        ? templateMeasuredValue.value
        : undefined;
    }
  }
  return undefined;
}

function getSexValue(value: string): SexType | null {
  return value === 'm' ? SexType.Male : value === 'f' ? SexType.Female : null;
}
